import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Dropdown, DropdownContent, DropdownItem, DropdownSelectTrigger } from '../../components-v2/Dropdown';
import { brandPrimary, brandPrimaryHover, grayIconFill, inputBorderColor, white } from '../../styles/colors';
import { SearchIcon } from '../svgs/icons/SearchIcon';
import { TextInput } from '../TextInput';

interface ICreatorOption {
	title: string;
	value: string | null;
}

interface IAutomationFiltersProps {
	searchText: string;
	selectedCreatorId: string | null;
	creatorOptions: ICreatorOption[];
	onSearchTextChange: (text: string) => void;
	onCreatorFilterChange: (creatorId: string | null) => void;
}

export const AutomationFilters: React.FC<IAutomationFiltersProps> = ({
	searchText,
	selectedCreatorId,
	creatorOptions,
	onSearchTextChange,
	onCreatorFilterChange,
}) => {
	const handleSearchChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			onSearchTextChange(e.target.value);
		},
		[onSearchTextChange]
	);

	const handleClearSearch = React.useCallback(() => {
		onSearchTextChange('');
	}, [onSearchTextChange]);

	const handleCreatorFilterChange = React.useCallback(
		(creatorId: string | null) => {
			onCreatorFilterChange(creatorId);
		},
		[onCreatorFilterChange]
	);

	const selectedCreatorOption = React.useMemo(() => {
		return creatorOptions.find(option => option.value === selectedCreatorId) || creatorOptions[0];
	}, [creatorOptions, selectedCreatorId]);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.searchContainer)}>
				<TextInput
					type='text'
					inputId='search-automations'
					placeholder='Search by automation name...'
					value={searchText}
					onChange={handleSearchChange}
					leftAccessory={<SearchIcon className={css(styleSheet.searchIcon)} fillColor={grayIconFill} />}
					rightAccessory={
						searchText ? (
							<button type='button' className={css(styleSheet.clearButton)} onClick={handleClearSearch}>
								Clear
							</button>
						) : null
					}
					className={css(styleSheet.searchInputWrapper)}
					inputClassName={css(styleSheet.searchInput)}
				/>
			</div>
			<div className={css(styleSheet.creatorFilterContainer)}>
				<Dropdown>
					<DropdownSelectTrigger styles={[styleSheet.dropdownTrigger]}>
						{selectedCreatorOption?.title || 'Show all creators'}
					</DropdownSelectTrigger>
					<DropdownContent styles={[styleSheet.dropdownContent]}>
						{creatorOptions.map(option => (
							<DropdownItem
								key={option.value || 'all'}
								onClick={() => handleCreatorFilterChange(option.value)}
								styles={[styleSheet.dropdownItem]}
							>
								{option.title}
							</DropdownItem>
						))}
					</DropdownContent>
				</Dropdown>
			</div>
		</div>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '16px',
		padding: '16px 16px 0 16px',
		borderRadius: '8px',
		marginBottom: '0',
		justifyContent: 'flex-end',
	},
	searchContainer: {
		flex: '0 0 300px',
		width: '300px',
	},
	searchInputWrapper: {
		background: white,
		borderRadius: '6px',
		border: `1px solid ${inputBorderColor}`,
		height: '44px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		':hover': {
			borderColor: brandPrimary,
		},
	},
	searchInput: {
		paddingLeft: 8,
	},
	searchIcon: {
		width: '20px !important',
		height: '20px !important',
		marginRight: 8,
		flexShrink: 0,
	},
	clearButton: {
		background: 'none',
		border: 'none',
		color: brandPrimary,
		cursor: 'pointer',
		fontSize: '14px',
		fontWeight: 500,
		padding: '4px 12px 4px 8px',
		borderRadius: '4px',
		':hover': {
			fontWeight: 700,
		},
	},
	creatorFilterContainer: {
		flex: '0 0 auto',
		minWidth: '200px',
	},
	dropdownTrigger: {
		height: '44px',
		width: '100%',
		borderRadius: '6px',
		border: `1px solid ${inputBorderColor}`,
		backgroundColor: white,
		padding: '12px',
		fontSize: '14px',
		color: '#4A4A4A',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		':hover': {
			borderColor: brandPrimary,
		},
	},
	dropdownContent: {
		maxHeight: '300px',
		overflowY: 'auto',
		overflowX: 'hidden',
	},
	dropdownItem: {
		padding: '8px 12px',
		fontSize: '14px',
		color: '#4A4A4A',
		cursor: 'pointer',
		':hover': {
			backgroundColor: brandPrimaryHover,
			color: white,
		},
	},
});
